"use client";

import { z } from "zod";
import { emailValidation, passwordValidation } from "@/lib/validators";
import { FieldErrors, useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { authClient } from "@/lib/auth-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import GoogleIcon from "@/components/icons/GoogleIcon";
import Link from "next/link";
import AuthEmailVerificationForm from "@/components/forms/AuthEmailVerificationForm";
import { useRouter, useSearchParams } from "next/navigation";
import { toast } from "@/components/ui/toast";
import { routes } from "@/lib/routes";

const signInFormSchema = z.object({
  email: emailValidation,
  password: passwordValidation,
});

export type SignInFormSchema = z.infer<typeof signInFormSchema>;

export default function SignInForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);

  const router = useRouter();
  const searchParams = useSearchParams();
  const error = searchParams.get("error");

  const form = useForm<SignInFormSchema>({
    resolver: zodResolver(signInFormSchema),
    defaultValues: {
      email: "",
    },
  });

  useEffect(() => {
    if (error) {
      if (error.includes("workspace")) {
        toast.error("You must be invited to a workspace before signing in");
      } else if (error.includes("signup_disabled")) {
        toast.error("You must be invited to a workspace before signing in");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    }
  }, [error]);

  const handleSignIn = async (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    const data = form.getValues();
    form.trigger("email").then(async isValid => {
      if (!isValid) {
        form.setError("email", {
          type: "manual",
          message: "Please enter a valid email",
        });
        return;
      }
      setIsLoading(true);
      const result = await authClient.emailOtp.sendVerificationOtp({
        email: data.email,
        type: "sign-in",
      });

      if (!result || result.error || !result.data.success) {
        toast.error(result?.error?.message || "Something went wrong. Please try again.");
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
      setStep(2);
    });
  };

  const onError = async (errors: FieldErrors<SignInFormSchema>) => {
    setIsLoading(false);
    console.error("❌ Form errors:", errors);
  };

  return step === 1 ? (
    <Form {...form}>
      <form className="flex flex-col gap-3 items-stretch" onSubmit={handleSignIn}>
        <FormDescription className="mb-5 text-primary heading-2xl">Sign in</FormDescription>
        <div className="flex flex-col gap-3 w-full">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="gap-1.5">
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input autoFocus {...field} type="email" autoComplete="email" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Button
          isLoading={isLoading}
          disabled={isLoading}
          variant="primary"
          onClick={handleSignIn}
          type={"button"}
          className="w-full"
        >
          Continue
        </Button>
        {step === 1 ? (
          <div className="flex flex-col gap-3">
            <div className="flex gap-2.5 items-center">
              <div className="flex flex-grow flex-shrink-0 border-1 border-border" />
              <span className="body-lg  text-secondary">or</span>
              <div className="flex flex-grow flex-shrink-0 border-1 border-border" />
            </div>
            <Button
              type="button"
              className="w-full"
              disabled={isLoading}
              onClick={async () => {
                setIsLoading(true);
                await authClient.signIn.social({
                  provider: "google",
                  errorCallbackURL: "/signin",
                  fetchOptions: {
                    onError: error => {
                      toast.error(error.error.message ?? "Something went wrong. Please try again.");
                      setIsLoading(false);
                    },
                  },
                });
              }}
            >
              {isLoading ? (
                "Connecting to Google..."
              ) : (
                <>
                  <GoogleIcon />
                  Sign in with Google
                </>
              )}
            </Button>
            <div className="flex items-center gap-1.5 text-secondary body-lg">
              Don&apos;t have an account?{" "}
              <Link className="text-link underline underline-offset-3" href="/create-account">
                Create an account
              </Link>
            </div>
          </div>
        ) : (
          <Button disabled={isLoading} className="w-full" type="button" onClick={() => setStep(1)}>
            Change email
          </Button>
        )}
      </form>
    </Form>
  ) : (
    <AuthEmailVerificationForm
      email={form.getValues("email")}
      onSuccess={async () => {
        const session = await authClient.getSession();
        router.push(session?.data?.user?.systemRole === "super" ? routes.admin : routes.root);
      }}
      onError={() => onError(form.formState.errors)}
    />
  );
}
